import React, { useRef, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

import { Modal, Icon, Button } from '@nearst/ui';

import styles from './UpgradeModal.module.scss';

import mixpanel from 'mixpanel-browser';

const UpgradeModal = ({ feature, showDialog, setShowDialog, children, channelId, shopId, onRegisterInterest }) => {
	const [isRegistering, setIsRegistering] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState(false);
	const submittingRef = useRef(false);
	const canRegisterInterest = channelId && shopId && onRegisterInterest;

	const close = () => {
		if (isRegistering) return;
		setShowDialog(false);
		if (submitted) setSubmitted(false);
		if (error) setError(false);
	};

	const confirm = async () => {
		if (submittingRef.current) return;
		submittingRef.current = true;

		try {
			mixpanel.track('Upgrade subscription contact click', { feature, channelId });

			if (canRegisterInterest) {
				setIsRegistering(true);
				setError(false);
				try {
					await onRegisterInterest(shopId, channelId);
					setSubmitted(true);
				} catch (err) {
					console.error('Failed to register interest:', err);
					setError(true);
				} finally {
					setIsRegistering(false);
				}
			} else {
				const subject = encodeURIComponent(`Upgrade to enable ${feature}`);
				window.location.href = `mailto:support@near.st?subject=${subject}`;
			}
		} finally {
			submittingRef.current = false;
		}
	};

	const mailtoHref = `mailto:support@near.st?subject=${encodeURIComponent(`Upgrade to enable ${feature}`)}`;

	return (
		<Modal className={styles.subscriptionModal} isOpen={showDialog} onDismiss={close} aria-label="subscription-modal">
			<div className={styles.subscriptionModalTitleContainer}>
				<h2 className={styles.subscriptionModalTitleText}>
					{submitted ? 'Interest registered' : `Upgrade your plan to enable ${feature}`}
				</h2>
				<button className={styles.subscriptionModalExitButton} data-cy="subscription-modal-close-button" onClick={close}>
					<Icon>clear</Icon>
				</button>
			</div>
			<div className={styles.subscriptionModalContainer}>
				{submitted ? (
					<div className={styles.successContent}>
						<CheckCircleIcon height={24} color="var(--green)" />
						<p>We've registered your interest in {feature}. A member of our customer success team will be in touch.</p>
						<p>
							If you'd like to follow up, you can also <a href={mailtoHref}>email support@near.st</a>.
						</p>
						<Button onClick={close} primary className={styles.subscriptionModalActionButton}>
							Close
						</Button>
					</div>
				) : (
					<div>
						{children}

						<Button onClick={confirm} primary className={styles.subscriptionModalActionButton} disabled={isRegistering}>
							Talk to our team
						</Button>
						{canRegisterInterest && (
							<p className={styles.helperText}>We'll register your interest and a member of our team will be in touch.</p>
						)}
						{error && (
							<p className={styles.errorText}>
								Something went wrong. Please try again or <a href={mailtoHref}>email support@near.st</a>.
							</p>
						)}
					</div>
				)}
			</div>
		</Modal>
	);
};

export { UpgradeModal };
